import {getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import React from 'react';

import {isExperimentalScratchPlatesEnabled} from 'analysis-tool/outputs/OutputsUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  Container,
  HydratedContainerTransferInfo,
  StorableSummary,
  TransferFlow,
} from 'constants/SampleConstants';
import {getBlockingStatusWarningTooltip} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {CheckSampleStatusParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import ContainerContentTables from 'samples/ContainerContentTables';
import ContainerDetailsPane, {FreezeThawInfo} from 'samples/ContainerDetailsPane';
import {ExpirationInfo} from 'samples/containers/ContainerExpirationDate';
import ContainerWellAttributionTable from 'samples/containers/ContainerWellAttributionTable';
import {nullableGraphQLStorableSummaryToREST} from 'samples/storables/graphql/translateStorable';
import StorableBanner from 'samples/storables/StorableBanner';
import StorableInfoTable from 'samples/storables/StorableInfoTable';
import * as InputSampleConstants from 'samples/transfers/input-sample/InputSampleConstants';
import TransferModal from 'samples/transfers/TransferModal';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import ModalAnchor from 'ui-library/ModalAnchor';

import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

interface ContainerTransferInfoDetailsProps {
  containerTransferInfo: HydratedContainerTransferInfo | null;
}

function ContainerTransferInfoDetails(props: ContainerTransferInfoDetailsProps): JSX.Element | null {
  if (!props.containerTransferInfo) {
    return null;
  }

  const inputStorables = props.containerTransferInfo.inputStorables.filter(
    (storable): storable is StorableSummary => storable != null
  );
  const outputStorables = props.containerTransferInfo.outputStorables.filter(
    (storable): storable is StorableSummary => storable != null
  );

  return (
    <Row>
      <Col md={6}>
        <h3>Transfers to Container</h3>
        <StorableInfoTable emptyTableText="No transfers to container" storables={inputStorables} />
      </Col>
      <Col md={6}>
        <h3>Transfers from Container</h3>
        <StorableInfoTable
          emptyTableText="No transfers from container"
          storables={outputStorables}
          dataTestAttrs={{component: 'ContainerView', element: 'TransfersFromContainer'}}
        />
      </Col>
    </Row>
  );
}

export interface ContainerViewProps {
  container: Container | null;
  containerTransferInfo: HydratedContainerTransferInfo | null;
  isWritable: boolean;
  isUnarchivable?: boolean;
  parentStorable: StorableSummaryGraphQL | null;
  expirationInfo: ExpirationInfo | null;
  freezeThawInfo: FreezeThawInfo | null;
  showFullView: boolean;
  reload?: () => void;
  /* When provided, this override supersedes writability of all fields regardless of what specific permissions
   * they care about. Leveraged to render a view only page in the transfer modal. */
  isReadOnlyOverride?: boolean;
}

interface State {
  isTransferModalOpen: boolean;
}

class ContainerView extends React.Component<ContainerViewProps, State> {
  static defaultProps = {
    showFullView: true,
    isWritable: false,
  };

  override state: State = {
    isTransferModalOpen: false,
  };

  private handleOpenTransferModal = (): void => {
    this.setState({isTransferModalOpen: true});
  };

  private handleCloseTransferModal = (): void => {
    this.setState({isTransferModalOpen: false});
  };

  private checkSampleStatus = async ({
    actionAttempted,
    eventGroupId,
  }: CheckSampleStatusParams): Promise<boolean> => {
    const {shouldProceed} = await callSampleStatusWarningModal({
      sampleIds: this.props.container ? [this.props.container.id] : [],
      trackingParams: {actionOrigin: ActionOrigin.INVENTORY_CONTAINER_PAGE, actionAttempted, eventGroupId},
    });
    return shouldProceed;
  };

  override render(): JSX.Element {
    if (!this.props.container) {
      return <LoadingFileScreen />;
    }
    return (
      <div {...getTestProps({component: 'ContainerView', element: 'root'})}>
        <ModalAnchor open={this.state.isTransferModalOpen}>
          <TransferModal
            flow={TransferFlow.TRANSFER_OUT}
            onRequestClose={this.handleCloseTransferModal}
            registryId={this.props.container.storableSchema.registryId}
            title="Add to containers"
            preselectedInputConfig={{
              inputType: InputSampleConstants.TYPE.EXISTING_CONTAINER,
              inputContainer: {
                ...this.props.container,
                containedItemIds: this.props.container.contents.map(({containedItemId}) => containedItemId),
              },
            }}
            actionOrigin={ActionOrigin.INVENTORY_CONTAINER_PAGE_TOOLBAR}
            onSuccess={this.props.reload}
          />
        </ModalAnchor>
        {this.props.showFullView && (
          <StorableBanner isWritable={this.props.isWritable} storable={this.props.container} />
        )}
        <div className={classNames({'react-tab-container': this.props.showFullView})}>
          <ContainerDetailsPane
            container={this.props.container}
            isWritable={this.props.isWritable}
            isReadOnlyOverride={this.props.isReadOnlyOverride}
            isUnarchivable={this.props.isUnarchivable}
            onRequestTransfer={this.handleOpenTransferModal}
            parentStorable={nullableGraphQLStorableSummaryToREST(this.props.parentStorable)}
            expirationInfo={this.props.expirationInfo}
            freezeThawInfo={this.props.freezeThawInfo}
            showFullView={this.props.showFullView}
            checkSampleStatus={this.checkSampleStatus}
            reload={this.props.reload}
          />
          <div className="u-spacing-top">
            <h3>Contents</h3>
            <ContainerContentTables
              containerId={this.props.container.id}
              checkSampleStatus={this.checkSampleStatus}
              disableEditsWithTooltip={getBlockingStatusWarningTooltip(this.props.container)}
              isWritable={this.props.isWritable}
            />
          </div>
          {this.props.showFullView && (
            <ContainerTransferInfoDetails containerTransferInfo={this.props.containerTransferInfo} />
          )}
          {this.props.showFullView &&
            isExperimentalScratchPlatesEnabled() &&
            this.props.container.isFixedPlateWell &&
            this.props.parentStorable?.__typename === 'PlateNode' &&
            this.props.container.gridCell && (
              <ContainerWellAttributionTable
                inventoryPlateId={this.props.parentStorable.id}
                rowIndex={this.props.container.gridCell.rowIndex}
                columnIndex={this.props.container.gridCell.columnIndex}
              />
            )}
        </div>
      </div>
    );
  }
}

export default ContainerView;
